import React, { useState } from "react";
import { FaEnvelope, FaLock, FaUserShield, FaKey, FaArrowLeft } from "react-icons/fa";
import Background3D from "../components/ui/Background3D";
import ThemeCustomizer from "../components/ui/ThemeCustomizer";
import { useTheme } from "../context/ThemeContext";

const ForgetPassword = () => {
  const [mode, setMode] = useState("reset");
  const [adminID, setAdminID] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [newAdminID, setNewAdminID] = useState("");
  const [message, setMessage] = useState({ content: "", type: "" });
  const [loading, setLoading] = useState(false);
  const { isDarkMode } = useTheme();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "adminID") setAdminID(value);
    else if (name === "newPassword") setNewPassword(value);
    else if (name === "confirmPassword") setConfirmPassword(value);
    else if (name === "newAdminID") setNewAdminID(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ content: "", type: "" });

    if (!adminID) {
      setMessage({ content: "Admin ID is required.", type: "danger" });
      return;
    }

    const allowedEmails = ['contact@bookscomply.com', 'contact@nareshco.com'];
    if (!allowedEmails.includes(adminID)) {
      setMessage({
        content: "This feature is only available for authorized admin accounts.",
        type: "danger",
      });
      return;
    }

    if (mode === "reset") {
      if (!newPassword || !confirmPassword) {
        setMessage({ content: "All fields are required.", type: "danger" });
        return;
      }
      if (newPassword !== confirmPassword) {
        setMessage({ content: "Passwords do not match.", type: "danger" });
        return;
      }
      if (newPassword.length < 6) {
        setMessage({ content: "Password must be at least 6 characters.", type: "danger" });
        return;
      }
    } else if (mode === "change") {
      if (!newAdminID) {
        setMessage({ content: "New Admin ID is required.", type: "danger" });
        return;
      }
      if (adminID === newAdminID) {
        setMessage({ content: "New Admin ID must be different from current.", type: "danger" });
        return;
      }
    }

    setLoading(true);

    try {
      const endpoint = mode === "reset"
        ? `${process.env.REACT_APP_API_BASE_URL}/api/admin/reset-password`
        : `${process.env.REACT_APP_API_BASE_URL}/api/admin/change-username`;

      const body = mode === "reset"
        ? { adminID, newPassword }
        : { currentAdminID: adminID, newAdminID };

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Operation failed.");
      }

      setMessage({
        content: mode === "reset"
          ? "Password reset successfully! You can now login with your new password."
          : `Admin ID changed successfully! Your new Admin ID is: ${data.newAdminID}`,
        type: "success",
      });

      setAdminID("");
      setNewPassword("");
      setConfirmPassword("");
      setNewAdminID("");
    } catch (error) {
      setMessage({ content: error.message, type: "danger" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 relative overflow-hidden transition-colors duration-300 ${isDarkMode ? 'dark bg-dark-950' : 'bg-mesh'}`}>
      <Background3D variant="auth" />

      <div className="absolute top-4 right-4 z-20">
        <ThemeCustomizer variant="auth" />
      </div>

      <div className="relative w-full max-w-lg z-10 animate-fade-in-up">
        <div className="glass-strong rounded-3xl shadow-3d overflow-hidden border border-white/40 dark:border-dark-600">
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-6 text-white text-center">
            <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
              <FaKey className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold">
              {mode === "reset" ? "Reset Password" : "Change Admin ID"}
            </h2>
            <p className="text-sm text-white/80 mt-1">
              {mode === "reset"
                ? "Authorized admin accounts only"
                : "Update your admin username"}
            </p>
          </div>

          <div className="p-6 md:p-8">
            <div className="flex gap-2 mb-6">
              <button
                type="button"
                onClick={() => { setMode("reset"); setMessage({ content: "", type: "" }); }}
                className={`flex-1 py-2.5 px-4 rounded-xl font-medium transition-all ${
                  mode === "reset"
                    ? "btn-premium !py-2.5"
                    : "bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-600"
                }`}
              >
                Reset Password
              </button>
              <button
                type="button"
                onClick={() => { setMode("change"); setMessage({ content: "", type: "" }); }}
                className={`flex-1 py-2.5 px-4 rounded-xl font-medium transition-all ${
                  mode === "change"
                    ? "btn-premium !py-2.5"
                    : "bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-600"
                }`}
              >
                Change Admin ID
              </button>
            </div>

            {message.content && (
              <div className={`p-4 rounded-xl mb-4 text-sm font-medium ${
                message.type === "success"
                  ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800"
                  : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800"
              }`} role="alert">
                {message.content}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <FaUserShield className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="adminID"
                  className="input-modern pl-12"
                  placeholder="Current Admin ID"
                  value={adminID}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {mode === "reset" && (
                <>
                  <div className="relative">
                    <FaLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="password"
                      name="newPassword"
                      className="input-modern pl-12"
                      placeholder="New Password"
                      value={newPassword}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="relative">
                    <FaLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="password"
                      name="confirmPassword"
                      className="input-modern pl-12"
                      placeholder="Confirm New Password"
                      value={confirmPassword}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </>
              )}

              {mode === "change" && (
                <div className="relative">
                  <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    name="newAdminID"
                    className="input-modern pl-12"
                    placeholder="New Admin ID"
                    value={newAdminID}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              )}

              <button type="submit" disabled={loading} className="btn-premium w-full py-3.5">
                {loading
                  ? (mode === "reset" ? "Resetting Password..." : "Changing Admin ID...")
                  : (mode === "reset" ? "Reset Password" : "Change Admin ID")}
              </button>
            </form>

            <div className="text-center mt-6">
              <a href="/login" className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-secondary-500 font-medium transition-colors">
                <FaArrowLeft className="w-3.5 h-3.5" />
                Back to Login
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgetPassword;
